import { getDateFormat } from "@frontend-shared/utils/date";
import { useDirection } from "@radix-ui/react-direction";
import { format } from "date-fns";
import i18next from "i18next";
import { Calendar as CalendarIcon } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface IStyledDatePickerProps {
  id?: string;
  wrapperClassName?: string;
  className?: string;
  isError?: boolean;
  placeholder?: string;
  value?: Date | string;
  onChange?: (date: Date | string) => void;
  disabled?: boolean;
  fromDate?: Date;
}

export const StyledDatePicker = React.forwardRef<
  HTMLButtonElement,
  IStyledDatePickerProps
>(
  (
    {
      id,
      wrapperClassName,
      className,
      isError,
      placeholder = "Select date",
      value,
      onChange,
      disabled,
      fromDate,
      ...props
    },
    ref,
  ) => {
    const dir = useDirection();
    const [isOpen, setIsOpen] = React.useState(false);

    const handleSelect = (date: Date | undefined) => {
      onChange?.(date ? date.toISOString() : "");
      setIsOpen(false);
    };

    const calendarDisabled =
      disabled || (fromDate ? { before: fromDate } : undefined);

    // NOTE: Date-only strings (yyyy-MM-dd) are parsed as UTC midnight by the
    // JS Date constructor, shifting the displayed date one day back in
    // negative-offset timezones. Appending T00:00:00 forces local-time parsing.
    const normalizedValue =
      typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? `${value}T00:00:00`
        : value;

    const selectedDate = value
      ? new Date(normalizedValue as string | Date)
      : undefined;

    return (
      <div className={cn("relative", wrapperClassName)}>
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={ref}
              id={id}
              variant="outline"
              disabled={disabled}
              className={cn(
                "bg-input-background h-12 w-full justify-between font-normal focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:border-ring px-3 hover:bg-input-background hover:text-foreground",
                isError &&
                  "border-destructive focus-visible:border-destructive focus-visible:ring-destructive",
                className,
              )}
              {...props}
            >
              {value ? (
                format(selectedDate!, getDateFormat(i18next.language))
              ) : (
                <span className="text-muted-foreground">{placeholder}</span>
              )}
              <CalendarIcon className="h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto overflow-hidden p-0" align="start">
            <Calendar
              dir={dir}
              mode="single"
              selected={selectedDate}
              captionLayout="dropdown"
              onSelect={handleSelect}
              disabled={calendarDisabled}
            />
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);

StyledDatePicker.displayName = "StyledDatePicker";
